import React from "react";
import { Input } from "@shared/ui/Input";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { HorizontalFormField } from "@shared/ui/HorizontalFormField";
import { Select } from "@shared/ui/Select";
import { IApplicationForm } from "@entities/Product/Application/types";
import { extractNumbers, setSpacesInNumber } from "@shared/lib";
import { useUnit } from "effector-react";
import {
  $realEstateAddresses,
  $realEstateDistricts,
  getAddressesFx,
  getDistrictsFx,
} from "@entities/Product/Application/model";
import { ISelectOption } from "@shared/types";
import { findOption } from "@entities/Product/Application/utils";

interface IProps {
  stateOptions: { value: string; label: string }[];
  pledgeTypes: { value: string; label: string }[];
}

export function CollateralRealEstateFields({
  stateOptions,
  pledgeTypes,
}: IProps) {
  const { t } = useTranslation();
  const { watch, setValue, control } = useFormContext<IApplicationForm>();
  const [districtOptions, addressOptions, getDistricts, getAddresses] = useUnit(
    [$realEstateDistricts, $realEstateAddresses, getDistrictsFx, getAddressesFx]
  );

  const [isDistrictsLoading, isAddressesLoading] = useUnit([
    getDistrictsFx.pending,
    getAddressesFx.pending,
  ]);

  const { real_estate_address_state } = watch();

  return (
    <>
      <HorizontalFormField label={t("Тип залога")}>
        <Controller
          control={control}
          name="pledge_type"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(pledgeTypes, field.value)}
              onChange={(newValue: ISelectOption) =>
                field.onChange(newValue.value)
              }
              errorMessage={fieldState.error?.message}
              options={pledgeTypes}
              placeholder={t(`Тип залога`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Стоимость недвижимости")}>
        <Controller
          name="real_estate_price"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              onChange={(e) =>
                field.onChange(
                  setSpacesInNumber(extractNumbers(e.target.value))
                )
              }
              inputMode="numeric"
              hasError={Boolean(fieldState.error?.message)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Город/область")}>
        <Controller
          control={control}
          name="real_estate_address_state"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(stateOptions, field.value)}
              onChange={(newValue: ISelectOption) => {
                field.onChange(newValue.value);
                getDistricts(newValue.value);
                setValue("real_estate_address_district", "");
                setValue("real_estate_address_village", "");
              }}
              hasError={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              options={stateOptions}
              placeholder={t(`Выберите город/область`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Город/регион")}>
        <Controller
          control={control}
          name="real_estate_address_district"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(districtOptions, field.value)}
              onChange={(newValue: ISelectOption) => {
                field.onChange(newValue.value);
                getAddresses({
                  districtCode: newValue.value,
                  stateCode: real_estate_address_state,
                });
                setValue("real_estate_address_village", "");
              }}
              hasError={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              isLoading={isDistrictsLoading}
              options={districtOptions}
              isDisabled={
                !watch("real_estate_address_state") || isDistrictsLoading
              }
              placeholder={t(`Выберите город/регион`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Населенный пункт")}>
        <Controller
          control={control}
          name="real_estate_address_village"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(addressOptions, field.value)}
              onChange={(newValue: ISelectOption) =>
                field.onChange(newValue.value)
              }
              hasError={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              options={addressOptions}
              isLoading={isAddressesLoading}
              isDisabled={
                !watch("real_estate_address_district") || isAddressesLoading
              }
              placeholder={t(`Выберите населенный пункт`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Улица, дом, квартира")}>
        <Controller
          name="real_estate_full_address"
          control={control}
          render={({ field, fieldState }) => (
            <Input {...field} hasError={Boolean(fieldState.error?.message)} />
          )}
        />
      </HorizontalFormField>
    </>
  );
}
